import {
  type api,
  type ResponseData,
  useInfiniteApiQuery,
  useTeam,
} from '@vrplatform/data';
import { formatAddress, getListingName } from '@vrplatform/ui-common';
import { SESSION_STICKY_VIEW_QUERY_OPTIONS } from '@vrplatform/data';
import { getContactUserAccess } from '../_utils';
import { useOwnersTableFilter } from './useOwnersTableFilter';

export type OwnersTableQueryType =
  | 'overview'
  | 'in-review'
  | 'in-review-banner';

type Params = {
  type: OwnersTableQueryType;
  skip?: boolean;
  refetchOnMount?: boolean;
  sessionSticky?: boolean;
};

type ApiContact = ResponseData<'get:/contacts'>['data'][number];

type Filter = ReturnType<typeof useOwnersTableFilter>['filter'];

export const getOwnersTableQueryFilter = ({
  filter,
  type,
  page,
}: {
  filter: Filter;
  type: OwnersTableQueryType;
  page: number;
}): NonNullable<api['/contacts']['get']['parameters']['query']> => {
  if (type === 'in-review-banner') {
    return {
      page,
      limit: 2,
      type: 'owner',
      status: 'pending',
    };
  }

  return {
    page,
    search: filter.search?.trim() || undefined,
    type: 'owner',
    isIndividual: filter.type ? filter.type === 'individual' : undefined,
    status: type === 'in-review' ? 'pending' : (filter.status ?? undefined),
  };
};

type ContactUserAccess = ReturnType<typeof getContactUserAccess>;

export type OwnerRow = ReturnType<typeof mapContactToRow>;

export const useOwnersTableQuery = ({
  type,
  skip,
  refetchOnMount,
  sessionSticky,
}: Params) => {
  const { filter } = useOwnersTableFilter();
  const [{ id: teamId }] = useTeam();

  return useInfiniteApiQuery(
    async ($api, signal, args, pageParam, hasura) => {
      const response = await $api.GET('/contacts', {
        params: {
          query: getOwnersTableQueryFilter({
            filter: args.filter,
            page: pageParam,
            type: args.type,
          }),
        },
        signal,
      });

      if (response.error) {
        throw response.error;
      }

      const contacts = response.data.data;
      const contactIds = contacts.map((contact) => contact.id);
      const listingIds = Array.from(
        new Set(
          contacts.flatMap(
            (contact) =>
              contact.activeOwnerships?.map(
                (ownership) => ownership.listingId
              ) ?? []
          )
        )
      );

      let listingNameById = new Map<string, string>();
      let userAccessById = new Map<string, ContactUserAccess | null>();

      if (contactIds.length || listingIds.length) {
        const { listings, contacts: accessContacts } = await hasura((q) => ({
          listings: listingIds.length
            ? q
                .listings({
                  where: {
                    id: {
                      _in: listingIds,
                    },
                  },
                })
                .map((listing) => ({
                  id: listing.id,
                  name: getListingName(listing),
                }))
            : [],
          contacts: contactIds.length
            ? q
                .contacts({
                  where: {
                    id: {
                      _in: contactIds,
                    },
                  },
                })
                .map((contact) => ({
                  id: contact.id,
                  userAccess: getContactUserAccess(contact, args.teamId),
                }))
            : [],
        }));

        listingNameById = new Map(
          listings.map((listing) => [listing.id, listing.name])
        );
        userAccessById = new Map(
          accessContacts.map((contact) => [contact.id, contact.userAccess])
        );
      }

      const list = contacts.map((contact) =>
        mapContactToRow(contact, {
          teamId: args.teamId,
          listingNameById,
          userAccessById,
        })
      );

      return {
        list,
        aggregate: response.data.pagination.total,
        nextPage: response.data.pagination.nextPage,
      };
    },
    {
      skip,
      refetchOnMount,
      queryGroup: 'owners',
      uniqueQueryKey: 'owners-table',
      variables: { teamId, filter, type },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
      ...(sessionSticky ? SESSION_STICKY_VIEW_QUERY_OPTIONS : {}),
    }
  );
};

export type OwnersTableQueryData = NonNullable<
  ReturnType<typeof useOwnersTableQuery>['data']
>;

const mapContactToRow = (
  contact: ApiContact,
  {
    teamId,
    listingNameById,
    userAccessById,
  }: {
    teamId: string;
    listingNameById: Map<string, string>;
    userAccessById: Map<string, ContactUserAccess | null>;
  }
) => {
  const getIsArchived = () => contact.status === 'inactive';

  const currentPeriodMemberships =
    contact.activeOwnerships?.map((ownership) => ({
      id: `${contact.id}-${ownership.listingId}`,
      listingName: listingNameById.get(ownership.listingId) || 'No name',
      listingId: ownership.listingId,
    })) ?? [];

  const companyType = contact.companyType ?? undefined;
  const address = contact.address ?? null;

  return {
    id: contact.id,
    name: contact.name,
    firstName: contact.firstName,
    currentPeriodMemberships,
    type: companyType ? 'company' : 'individual',
    companyType,
    address: formatAddress(
      address
        ? {
            line1: address.line1 ?? '',
            line2: address.line2 ?? undefined,
            city: address.city ?? '',
            postcode: address.postalCode ?? '',
            state: address.state ?? undefined,
            stateCode: address.stateCode ?? undefined,
            country: undefined,
            countryCode: address.countryCode ?? undefined,
          }
        : {
            line1: '',
            line2: undefined,
            city: '',
            postcode: '',
            state: undefined,
            stateCode: undefined,
            country: undefined,
            countryCode: undefined,
          }
    ),
    tenantId: teamId,
    isArchived: getIsArchived(),
    userAccess: userAccessById.get(contact.id) ?? null,
    email: contact.email ?? undefined,
    connectionId: contact.connectionId ?? undefined,
  };
};
